//----------------------------------------------------------------------------------------------------------------------
// Abstract Level Class
//----------------------------------------------------------------------------------------------------------------------

import { Scene } from '@babylonjs/core';
import { ClusteredLightContainer } from '@babylonjs/core/Lights/Clustered/clusteredLightContainer';

import type { LoggerInterface } from '../interfaces/logger.ts';
import type {
    LevelConfig,
    LevelContext,
    LevelInstance,
    PropertyHandler,
} from '../interfaces/level.ts';

import { SAGELogger } from '../utils/logger.ts';
import type { GameEngine } from './gameEngine.ts';
import { OutlineManager } from '../managers/outline.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Abstract base class for game levels.
 *
 * Levels are responsible for creating and configuring their own scenes via the
 * `buildScene()` method. All runtime dependencies (eventBus, sceneEngine, entityManager)
 * are injected via the constructor and are immediately available.
 *
 * Users should not instantiate Level subclasses directly. Instead, use LevelManager
 * which acts as a factory and injects the required dependencies:
 *
 * ```typescript
 * // Register a custom level class
 * levelManager.registerLevelClass('my-level', MyCustomLevel);
 *
 * // Load the level - manager creates instance with deps injected
 * const level = await levelManager.loadLevel({ name: 'Level1', class: 'my-level' });
 * ```
 */
export abstract class Level implements LevelInstance
{
    public readonly name : string;
    protected readonly _log : LoggerInterface;
    protected readonly _context : LevelContext;
    protected _scene : Scene | null = null;
    public clusteredLights : ClusteredLightContainer | null = null;
    public outlines : OutlineManager | null = null;

    //------------------------------------------------------------------------------------------------------------------
    // Constructor
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Creates a new Level instance.
     *
     * @param config
     * @param context
     */
    constructor(config : LevelConfig, context : LevelContext)
    {
        this.name = config.name;
        this._context = context;
        this._log = context.logger?.getLogger(`Level:${ config.name }`)
            ?? new SAGELogger(`Level:${ config.name }`, 'info');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Accessors
    //------------------------------------------------------------------------------------------------------------------

    get scene() : Scene | null
    {
        return this._scene;
    }

    get isLoaded() : boolean
    {
        return this._scene !== null;
    }

    get gameEngine() : GameEngine
    {
        return this._context.gameEngine;
    }

    get propertyHandlers() : Map<string, PropertyHandler>
    {
        return this._context.propertyHandlers;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Protected Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Emit a progress event during loading
     *
     * @param progress - Percentage (0-100)
     * @param message
     */
    protected $emitProgress(progress : number, message : string) : void
    {
        this.gameEngine.eventBus.publish({
            type: 'level:progress',
            payload: {
                levelName: this.name,
                progress,
                message,
            },
        });
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Load and create the scene for this level.
     * Progress events will be emitted during loading.
     */
    async load() : Promise<Scene>
    {
        if(this._scene)
        {
            this._log.warn(`Level ${ this.name } is already loaded`);
            return this._scene;
        }

        this._log.info(`Loading level: ${ this.name }`);
        this._log.time(`level-${ this.name }-load`);

        try
        {
            this.$emitProgress(0, 'Starting level load...');

            // Let the concrete level build its content
            this._scene = await this.buildScene();

            this.$emitProgress(100, 'Level loaded successfully');
            this.gameEngine.eventBus.publish({
                type: 'level:complete',
                payload: {
                    levelName: this.name,
                    message: 'Level loaded successfully',
                    level: this,
                },
            });

            this._log.timeEnd(`level-${ this.name }-load`);
            this._log.info(`Level ${ this.name } loaded successfully`);

            return this._scene;
        }
        catch (error)
        {
            this._log.error(`Failed to load level ${ this.name }:`, error);
            this.gameEngine.eventBus.publish({
                type: 'level:error',
                payload: {
                    levelName: this.name,
                    message: 'Failed to load level',
                    error,
                },
            });
            throw error;
        }
    }

    /**
     * Abstract method for building the scene content.
     * Concrete levels must implement this to create their specific content.
     *
     * All dependencies are available via `this.gameEngine`.
     *
     */
    protected abstract buildScene() : Promise<Scene>;

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Called when this level becomes the active level during a transition.
     * Override in subclasses to perform activation logic (e.g., start music, show UI).
     * Default implementation is a no-op.
     */
    onActivate?() : Promise<void> | void;

    /**
     * Called when this level is being replaced during a transition.
     * Override in subclasses to perform cleanup logic (e.g., fade out, save state).
     * Default implementation is a no-op.
     */
    onDeactivate?() : Promise<void> | void;

    /**
     * Dispose of this level's resources and scene.
     *
     * The `$` prefix indicates this is an internal lifecycle method, consistent with
     * other engine lifecycle methods like `$teardown()`.
     */
    async $dispose() : Promise<void>
    {
        if(this._scene)
        {
            this._log.info(`Disposing level: ${ this.name }`);

            // Detach all entities from this scene's nodes BEFORE disposing the scene.
            // Behaviors need the scene alive to clean up physics bodies, cameras, etc.
            const entityManager = this._context.gameEngine?.managers?.entityManager;
            if(entityManager?.getAllEntities)
            {
                for(const entity of entityManager.getAllEntities())
                {
                    if(entity.node && entity.node.getScene() === this._scene)
                    {
                        entityManager.detachFromNode(entity);
                    }
                }
            }

            if(this.outlines)
            {
                this.outlines.dispose();
                this.outlines = null;
            }

            if(this.clusteredLights)
            {
                this.clusteredLights.dispose();
                this.clusteredLights = null;
            }

            this._scene.dispose();
            this._scene = null;
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------
